import { z } from 'zod';

export enum Country {
  SWEDEN = 104,
  DENMARK = 555,
  FINLAND = 505,
  POLAND = 105,
  NORWAY = 250,
  CONFIDENTIAL = 999,
}

export const CountryNumericCode = {
  SWEDEN: 104,
  DENMARK: 555,
  FINLAND: 505,
  POLAND: 105,
  NORWAY: 250,
  CONFIDENTIAL: 999,
};

export const partnerSchema = z.object({
  id: z.string(),
  fireId: z.string().nullable().optional(),
  dwKey: z.string(),
  parentId: z.union([z.number(), z.string()]).nullable(),
  businessAddressId: z.string().optional(),
  sgtId: z.number(),
  name: z.string(),
  childCount: z.number().optional(),
  locationCount: z.number(),
  companyId: z.enum(Country),
  customerText: z.string(),
  customerDescription: z.string(),
  customerIdAndDescription: z.string(),
  street: z.string(),
  postalCode: z.string(),
  city: z.string(),
  countryId: z.string(),
  countryName: z.string(),
  description: z.string(),
  additionalDescription: z.string(),
  additionalAddressInfo: z.string(),
  pickupAddressId: z.string(),
  isActivePickupPlace: z.boolean(),
  isActive: z.boolean(),
  type: z.string(),
  hasActiveChildren: z.boolean().optional(),
  source: z.string(),
  modifiedDate: z.string().optional(),
  billable: z.boolean(),
  isPriceAgreed: z.boolean(),
  isLocation: z.boolean().optional(),
  isCS: z.boolean().optional(),
  organizationNumber: z.string(),
  siteId: z.string(),
  hasRnpAccess: z.boolean(),
  singleDeliveryAddress: z.boolean().optional(),
  /**
   * The business system this Partner transacts against. Computed when the partner API
   * serialises a Partner, never persisted, so it cannot drift out of agreement with
   * `source`.
   */
  erp: z.enum(['FIRE', 'FNO', 'NAVISION', 'CS']).optional(),
});

export type Partner = z.infer<typeof partnerSchema>;

export const PartnerSource = {
  FNO: 'FNO',
  'DL-FIRE': 'DL-FIRE',
  CS: 'CS',
  DL_FI: 'DL_FI',
  NAVISION_NO: 'NAVISION_NO',
  NAVISION_FI: 'NAVISION_FI',
  NAVISION_DK: 'NAVISION_DK',
  NAVISION_PL: 'NAVISION_PL',
  FIRE: 'FIRE',
  'N/A': 'N/A',
} as const;

export type PartnerSourceValues = keyof typeof PartnerSource;

/** The business system a Partner transacts against. */
export const PartnerErp = {
  FIRE: 'FIRE',
  FNO: 'FNO',
  NAVISION: 'NAVISION',
  CS: 'CS',
} as const;

export type PartnerErpValues = (typeof PartnerErp)[keyof typeof PartnerErp];

/** The upstream system a Partner record itself came from. */
export const RecordOrigin = {
  DL: 'DL',
  CS: 'CS',
  EDW: 'EDW',
} as const;

export type RecordOriginValues = (typeof RecordOrigin)[keyof typeof RecordOrigin];

/** Where a Partner's aggregated order-transaction rows live. */
export const OrderTransactionStore = {
  OP: 'OP',
  EDW: 'EDW',
} as const;

export type OrderTransactionStoreValues =
  (typeof OrderTransactionStore)[keyof typeof OrderTransactionStore];

export const OrderTransactionStoreMix = {
  ALL_OP: 'ALL_OP',
  ALL_EDW: 'ALL_EDW',
  MIXED: 'MIXED',
} as const;

export type OrderTransactionStoreMixValues =
  (typeof OrderTransactionStoreMix)[keyof typeof OrderTransactionStoreMix];
